import _ from "underscore";
import Translator from "../../utils/translator";
import { Dialog } from "../framework/dialog";
import { show } from "../../utils/vanilla";
import { getPin, setPin, removePin, setNoPin, NO_PIN_VALUE } from "./inspectionHelper";
import template from "./inspectionLock.tmpl";
import { DriveStore } from "../../store/reducers";

const ERROR_MESSAGES = {
    pinInvalid: "The PIN must be a 4-digit number. Please try again.",
    pinFailConfirm: "The PIN does not match the first one you entered. Please try again.",
    pinWrong: "The PIN is incorrect. Please try again."
};

const BUTTON_STRINGS = ["No PIN", "Set PIN"];

export default class InspectionLockDialog {
    private readonly translator: Translator;
    private readonly language?: string;
    private readonly isEnteringInspection: boolean;
    private readonly changeButtonBind: (e: any) => void;
    private successCallback?: (() => void) | null;
    private errorCallback?: (() => void) | null;
    private logoutCallback?: (() => void) | null;
    private isConfirm: boolean;
    private dialog: Dialog;
    private pinStrings: string[];
    private pinInputElement?: HTMLInputElement;
    private readonly store: DriveStore;

    constructor(
        store: DriveStore,
        private readonly currentUser,
        isEnteringInspection: boolean,
        translator: Translator,
        language?: string,
        successCallback?: () => void,
        errorCallback?: () => void,
        logoutCallback?: () => void
    ) {
        this.store = store;
        this.isEnteringInspection = isEnteringInspection;
        this.translator = translator;
        this.language = language;
        this.successCallback = successCallback;
        this.errorCallback = errorCallback;
        this.logoutCallback = logoutCallback;
        this.changeButtonBind = this.changeButtonText.bind(this);
    }

    open() {
        return this.translator.translate(BUTTON_STRINGS, this.language)
            .then(translatedStrings => {
                this.pinStrings = translatedStrings;
                this.prepareDialog();
            });
    }

    close(removeCallbacks = true, rejectPromise = false) {
        this.pinInputElement && this.pinInputElement.removeEventListener("keyup", this.changeButtonBind);
        this.pinInputElement = undefined;

        // If closing dialog because driving detected and pin removed, need to call error callback
        // to correctly manage promise in inspection/main onBeforeUnload
        rejectPromise && this.errorCallback && this.errorCallback();
        this.dialog && this.dialog.remove();
        removeCallbacks && this.remove();
    }

    remove() {
        this.successCallback = null;
        this.errorCallback = null;
        this.logoutCallback = null;
    }

    private getPin() {
        let pin = "";
        if (this.pinInputElement && this.pinInputElement.value) {
            let inputValue = this.pinInputElement.value;
            let maxLength = Math.min(inputValue.length, 4);
            let inputCharacter: string;

            for (let i = 0; i < maxLength; i++) {
                inputCharacter = inputValue.charAt(i);
                if (!isNaN(parseInt(inputCharacter))) {
                    pin += inputCharacter;
                }
            }
        }

        return pin;
    }

    private onError() {
        let errorMessage = document.getElementById("pin-warning") as HTMLElement;
        if (errorMessage && this.dialog) {
            show(errorMessage);
            this.dialog.resize();
        }
    }

    private prepareDialog(tentativePin?: string) {
        let currentUserId = this.currentUser ? this.currentUser.id : undefined;
        let storedPin = getPin(this.store, currentUserId, true);
        let userSetPin = storedPin && storedPin !== NO_PIN_VALUE ? storedPin : undefined;
        let isInitial = this.isEnteringInspection;
        this.isConfirm = !!tentativePin;
        let showPinInput = isInitial || this.isConfirm || !!userSetPin;
        let showLogoutButton = !isInitial && !!userSetPin;
        let errorString = "";
        let successCallback: () => void;

        if (this.isEnteringInspection) {
            if (storedPin) {
                // pin set, no dialog needed on the way in
                return;
            } else {
                errorString = tentativePin ? ERROR_MESSAGES.pinFailConfirm : ERROR_MESSAGES.pinInvalid;
                successCallback = this.getEnterInspectionCallback(tentativePin, currentUserId);
            }
        } else {
            errorString = storedPin ? ERROR_MESSAGES.pinWrong : "";
            successCallback = this.getExitInspectionCallback(userSetPin, currentUserId);
        }

        this.openDialog(isInitial, showPinInput, errorString, showLogoutButton, successCallback);
    }

    private getEnterInspectionCallback(userSetPin?: string, userId?: string) {
        let successAndClose = () => {
            this.successCallback && this.successCallback();
            this.close();
        };

        return !userSetPin
            ? () => {
                let pin = this.getPin();
                if (!pin) {
                    setNoPin(this.store, userId);
                    successAndClose();
                } else if (pin.length === 4) {
                    this.close(false);
                    this.prepareDialog(pin);
                } else {
                    this.onError();
                }
            } : () => {
                let secondPin = this.getPin();
                if (userSetPin !== secondPin) {
                    this.onError();
                } else {
                    setPin(this.store, userSetPin, userId);
                    successAndClose();
                }
            };
    }

    private getExitInspectionCallback(storedPin?: string, userId?: string) {
        let successAndClose = () => {
            removePin(this.store, userId);
            this.successCallback && this.successCallback();
            this.close();
        };

        return !storedPin
            ? () => {
                successAndClose();
            } : () => {
                let inputPin = this.getPin();
                if (storedPin !== inputPin) {
                    this.onError();
                } else {
                    successAndClose();
                }
            };
    }

    private openDialog(isInitial: boolean, showPinInput: boolean, errorMessage: string, showLogoutButton: boolean, successCallback: () => void) {
        let buttonText = isInitial ? (this.isConfirm ? "Confirm PIN" : "No PIN") : "Continue";
        let templateStr = _.template(template)({
            isInitial,
            isConfirm: this.isConfirm,
            showPinInput,
            errorMessage
        });
        let errorCallback = () => {
            this.errorCallback && this.errorCallback();
            this.close();
        };
        let logoutCallback = () => {
            this.logoutCallback && this.logoutCallback();
            this.close();
        };

        let buttons = [
            [{
                id: this.isConfirm ? "confirmPin" : "confirmContinue", // For BQ logging purposes
                text: buttonText,
                className: "",
                callback: successCallback
            }],
            [{
                id: "confirmCancel",
                text: "Cancel",
                className: "button_secondary",
                callback: errorCallback
            }]
        ];

        if (showLogoutButton) {
            buttons[0].splice(0, 0, {
                id: "confirmLogout",
                text: "Log out",
                className: "",
                callback: logoutCallback
            });
        } else {
            buttons = [buttons[1].concat(buttons[0])];
        }

        this.dialog && this.close(false);
        this.dialog = new Dialog("inspection-mode-dialog", templateStr, this.translator, "", buttons, { closable: false });
        void this.dialog.open(this.translator, this.language)
            .then(() => {
                if (showPinInput) {
                    this.pinInputElement = document.querySelector("#pin-input") as HTMLInputElement;
                    this.pinInputElement && this.pinInputElement.addEventListener("keyup", this.changeButtonBind);
                }
            });
    }

    private changeButtonText() {
        if (this.pinInputElement) {
            this.pinInputElement.value = this.getPin();

            if (this.isEnteringInspection && !this.isConfirm) {
                let continueButton = document.getElementById("confirmContinue") as HTMLButtonElement;
                if (continueButton) {
                    if (this.pinInputElement.value) {
                        continueButton.textContent = this.pinStrings[1];
                    } else {
                        continueButton.textContent = this.pinStrings[0];
                    }
                }
            }
        }
    }
}